import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Quote } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RemoveStoryButton } from "@/components/RemoveStoryButton";

export default function Stories() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", before: "", after: "", author_name: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    () =>
      api
        .get("/stories")
        .then((r) => setStories(r.data.stories))
        .catch((err) => console.error("Failed to load stories:", err)),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (form.title.length < 3 || form.body.length < 20) {
      toast.error("Give the story a real title and at least 20 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/stories", form);
      toast.success("Your shift is now part of the field.");
      setOpen(false);
      setForm({ title: "", body: "", before: "", after: "", author_name: "" });
      load();
    } catch {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 sm:pt-24" data-testid="stories-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-10 sm:py-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-12">
          <div>
            <div className="eyebrow mb-4">Shift Stories</div>
            <h1 className="font-serif text-[2.25rem] leading-[1.05] sm:text-6xl sm:leading-tight max-w-2xl">
              The room where the <span className="italic text-accent">becoming</span> gets witnessed.
            </h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="w-full sm:w-auto btn-pill font-mono tracking-widest uppercase text-xs gap-2 h-12 px-6"
                data-testid="share-story-btn"
              >
                <Plus className="h-4 w-4" /> Share your Shift
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg w-[calc(100%-2rem)]" data-testid="share-story-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl sm:text-2xl">Share your Shift</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-12" data-testid="story-title-input" />
                <Textarea placeholder="What shifted for you?" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="story-body-input" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Before" value={form.before} onChange={(e) => setForm({ ...form, before: e.target.value })} className="h-12" data-testid="story-before-input" />
                  <Input placeholder="After" value={form.after} onChange={(e) => setForm({ ...form, after: e.target.value })} className="h-12" data-testid="story-after-input" />
                </div>
                <Input placeholder="Name (or leave blank)" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="h-12" data-testid="story-author-input" />
                <Button onClick={submit} disabled={submitting} className="w-full btn-pill font-mono uppercase text-xs tracking-widest h-12" data-testid="story-submit-btn">
                  {submitting ? "Sending..." : "Submit"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {stories.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="border border-border p-6 sm:p-8 hover:border-accent/40 transition-colors relative"
              data-testid={`story-card-${s.id}`}
            >
              <Quote className="h-5 w-5 text-accent mb-4 opacity-60" />
              {user?.is_owner && (
                <RemoveStoryButton story={s} onRemoved={(id) => setStories((prev) => prev.filter((x) => x.id !== id))} />
              )}
              <h3 className="font-serif text-xl sm:text-2xl leading-snug mb-3 sm:mb-4">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5 sm:mb-6">{s.body}</p>
              <div className="hairline pt-4 flex items-center justify-between flex-wrap gap-2">
                <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
                  {s.author_name}
                </span>
                {s.before && s.after && (
                  <span className="font-mono text-[10px] tracking-widest uppercase text-accent">
                    {s.before} → {s.after}
                  </span>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
